import { Loader2, PhoneCallIcon, Video, X } from "lucide-react";
import { Call, useBackupCall } from "@/lib/api/calls.ts";
import { formatDate, formatDuration } from "@/lib/utils.ts";
import { useState } from "react";
import { Button } from "@/components/ui/button.tsx";
import { useSignedMedia } from "@/lib/hooks/use-signed-media.ts";
import { useToast } from "@/components/ui/use-toast.ts";

interface CallTimelineItemProps {
  call: Call;
}

export function CallTimelineItem({ call }: CallTimelineItemProps) {
  const { toast } = useToast();
  const { mutateAsync: fetchRecording, isPending } = useBackupCall();

  const [showVideoPlayer, setShowVideoPlayer] = useState(false);
  const isScheduled = call.status === "scheduled";

  // const isEnded = call.status === "ended";
  const callCreator = call.fromUser?.name;

  const callDuration = formatDuration(call.duration);

  const handleFetchRecording = async () => {
    // Implement the method to fetch the recording here
    toast({
      title: "Fetching recording...",
      description: "This may take a few seconds",
      variant: "default",
    });

    await fetchRecording({
      callId: call.id,
    })
      .then(() => {
        toast({
          title: "Recording fetched",
          description: "The recording has been fetched successfully",
          variant: "default",
        });
      })
      .catch(() => {
        toast({
          title: "Recording is not available",
          description: "Please try again later or contact the support.",
          variant: "default",
        });
      });
  };

  if (isScheduled) {
    return null;
  }

  return (
    <div className="inline-flex w-full flex-col items-start justify-start gap-2.5 [&:not(:first-child)]:mt-4">
      <div className="inline-flex items-center justify-start gap-2.5 self-stretch">
        <div className="flex items-center justify-start gap-2.5">
          <div className="flex items-center justify-start gap-2.5 rounded bg-indigo-100">
            <div className="flex items-center justify-start gap-2.5 p-1">
              <PhoneCallIcon size={12} />
            </div>
          </div>
          <div className="text-xs font-normal text-stone-500">
            Call from {callCreator} on{" "}
            {formatDate(call.created_at || Date.now())}
          </div>
        </div>
      </div>
      <div className="inline-flex items-start justify-start gap-2.5 self-stretch">
        <div className="flex w-4 items-start justify-start gap-2.5 self-stretch px-2">
          <div className="w-20 origin-top-left rotate-0 self-stretch border border-zinc-300"></div>
        </div>
        <div className="w-full rounded-md bg-white p-4">
          <div>Call duration: {callDuration}</div>

          {call.recording_url && call.recording_url !== "no recording" && (
            <Button
              variant="outline"
              size="sm"
              className="mt-2"
              onClick={() => setShowVideoPlayer(!showVideoPlayer)}
            >
              <Video className="mr-2 h-4 w-4" /> View recording
            </Button>
          )}

          {(call.recording_url === null ||
            call.recording_url === "no recording") && (
            <Button
              variant="outline"
              size="sm"
              className="mt-2"
              onClick={handleFetchRecording}
              disabled={isPending}
            >
              {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Fetch recording
            </Button>
          )}

          {showVideoPlayer &&
            call.recording_url &&
            call.recording_url !== "no recording" && (
              <div className="mt-4">
                <div className="mb-2 flex items-center justify-between">
                  <h3 className="text-lg font-semibold">Recording</h3>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowVideoPlayer(false)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                <VideoPlayer recordingUrl={call.recording_url} />
              </div>
            )}
        </div>
      </div>
    </div>
  );
}

function VideoPlayer({ recordingUrl }: { recordingUrl: string }) {
  const { url, isPending, isError } = useSignedMedia(recordingUrl);

  if (isPending) {
    return (
      <div className="flex h-32 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex h-32 items-center justify-center">
        Error loading video
      </div>
    );
  }

  return (
    <video src={url} controls className="w-full rounded-md">
      Your browser does not support the video tag.
    </video>
  );
}
